/**
 * Hook composing AudioPermissionService, AudioRecordingManager, and AudioTranscriptionClient
 * into a single voice recording flow with auto-stop on silence.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { audioPermissionService } from '@/lib/audio/AudioPermissionService';
import { audioRecordingManager } from '@/lib/audio/AudioRecordingManager';
import { audioTranscriptionClient } from '@/lib/audio/AudioTranscriptionClient';
import type { VoiceRecordingState } from '@/types/voice-transcription';

interface UseVoiceRecordingResult {
  /** Current state of the voice recording flow */
  recordingState: VoiceRecordingState;
  /** The most recent transcribed text (null if none yet) */
  transcribedText: string | null;
  /** Error message if something went wrong */
  error: string | null;
  /** Start a new recording (requests permission if needed) */
  startRecording: () => Promise<void>;
  /** Manually stop the current recording and transcribe */
  stopRecording: () => Promise<void>;
  /** Clear the transcribed text and error state */
  clearTranscription: () => void;
}

export function useVoiceRecording(): UseVoiceRecordingResult {
  const [recordingState, setRecordingState] = useState<VoiceRecordingState>('idle');
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      audioRecordingManager.cleanup();
    };
  }, []);

  const transcribeFile = useCallback(async (fileUri: string | null) => {
    if (!mountedRef.current) return;

    if (!fileUri) {
      setRecordingState('idle');
      setError("Couldn't capture audio, try again");
      return;
    }

    setRecordingState('transcribing');
    setError(null);
    console.log('[useVoiceRecording] Starting transcription for:', fileUri);

    try {
      const text = await audioTranscriptionClient.transcribe(fileUri);
      console.log('[useVoiceRecording] Transcription result:', text);

      if (!mountedRef.current) return;

      if (!text || text.trim().length === 0) {
        console.warn('[useVoiceRecording] Empty transcription result');
        setRecordingState('idle');
        setError("Couldn't hear anything, try again");
        return;
      }

      setTranscribedText(text.trim());
      setRecordingState('idle');
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('[useVoiceRecording] Transcription error:', errorMsg);
      setRecordingState('idle');
      setError(`Transcription failed: ${errorMsg}`);
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setTranscribedText(null);

    const hasPermission = await audioPermissionService.ensurePermission();
    if (!hasPermission) {
      setError('Microphone permission is required');
      return;
    }

    try {
      await audioRecordingManager.startRecording({
        onStop: (fileUri) => {
          // Called when recording auto-stops (silence or max duration)
          if (mountedRef.current) {
            transcribeFile(fileUri);
          }
        },
      });

      if (mountedRef.current) {
        setRecordingState('recording');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[useVoiceRecording] Recording start error:', err);
      setRecordingState('idle');
      setError('Failed to start recording');
    }
  }, [transcribeFile]);

  const stopRecording = useCallback(async () => {
    // Manual stop — stopRecording returns URI and triggers onStop callback
    await audioRecordingManager.stopRecording();
    // The onStop callback from startRecording handles transcription
  }, []);

  const clearTranscription = useCallback(() => {
    setTranscribedText(null);
    setError(null);
  }, []);

  return {
    recordingState,
    transcribedText,
    error,
    startRecording,
    stopRecording,
    clearTranscription,
  };
}
